import React from 'react';
import { css, cx } from 'styled-system/css';
import { emphasis } from 'styled-system/recipes';

import { fill, type Text } from '@shaojiang/content';
import { DESIGN_IDS, type DesignId } from '@shaojiang/designs/config';
import { DesignVariants } from '@shaojiang/designs/runtime/DesignOnly';

type Values = Record<string, string | number>;

/** Renders `*word*` as an emphasized word (marker highlight or gradient text, per design). */
const keepTogether = css({ whiteSpace: 'nowrap' });

interface EmphProps {
  children: string;
  values?: Values;
  /** Never break the emphasized words across lines (e.g. "AI-native") */
  nowrap?: boolean;
}

export function Emph({ children, values, nowrap = false }: EmphProps) {
  const text = values ? fill(children, values) : children;
  const parts = text.split(/\*([^*]+)\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={cx(emphasis(), nowrap && keepTogether)}>
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

/** Designs in which a piece of copy has any words (some copy only exists in one design). */
export const designsWith = (text: Text): DesignId[] =>
  typeof text === 'string' ? (text ? [...DESIGN_IDS] : []) : DESIGN_IDS.filter((id) => text[id]);

/**
 * A piece of site copy, worded for the active design. Copy that is the same in every design
 * renders as plain text, without wrappers.
 */
export function Copy({ text, values }: { text: Text; values?: Values }) {
  if (typeof text === 'string') return <Emph values={values}>{text}</Emph>;
  const words = DESIGN_IDS.map((id) => text[id]);
  if (words.every((w) => w === words[0])) return <Emph values={values}>{words[0]}</Emph>;
  const variants = Object.fromEntries(
    DESIGN_IDS.map((id) => [
      id,
      <Emph key={id} values={values}>
        {text[id]}
      </Emph>,
    ]),
  );
  return <DesignVariants as='span' {...variants} />;
}

/** Plain string for attributes (aria-label, alt, title): the default design's wording. */
export const plain = (text: Text, values?: Values) => {
  const raw = typeof text === 'string' ? text : text[DESIGN_IDS[0]];
  return (values ? fill(raw, values) : raw).replace(/\*/g, '');
};
